import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import WYSIWYGEditor from '../components/forms/WYSIWYGEditor';
import { Plus, Trash2, Star, Users, Phone, MapPin } from 'lucide-react';
import Modal from '../components/ui/Modal';
import SearchableSelect from '../components/ui/SearchableSelect';
import apiService from '../services/api';
import { CompanyUser, WorkOrderVendor, VendorSearchDto, WorkOrderTemplate, Category, ClientLocationDto, VendorCompliance } from '../types';
import toast from 'react-hot-toast';
import VendorSelectionModal from '../components/modals/VendorSelectionModal';
import ChangeWorkOrderClientModal from '../components/workorder/ChangeWorkOrderClientModal';
import ComplianceOverrideModal from '../components/modals/ComplianceOverrideModal';
import { formatPhoneNumber } from '../utils/formatters';
import { getComplianceStatusClasses } from '../utils/vendorCompliance';
import { useSubscription } from '../contexts/SubscriptionContext';
import { hexToTailwindClasses } from '../utils/badges';
import PriorityDropdown from '../components/forms/PriorityDropdown';

const schema = yup.object({
  assignedUserId: yup.string().optional(),
  priorityId: yup.number().optional().min(0, 'Please select a valid priority'),
  statusId: yup.number().optional().min(0, 'Please select a valid status'),
  categoryId: yup.number().optional(),
  equipmentId: yup.number().optional(),
  dueDate: yup.string().required('Due date is required'),
  externalWorkOrderNumber: yup.string(),
  nteAmount: yup.number().optional().nullable().min(0, 'NTE amount must be positive'),

  templateId: yup.number().optional(),
  description: yup.string()
    .required('Description is required')
    .test('min-length', 'Description must be at least 10 characters', function(value) {
      if (!value) return false;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = value;
      const textLength = tempDiv.textContent?.length || 0;
      return textLength >= 10;
    })
});

type FormData = yup.InferType<typeof schema>;

const EditWorkOrderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showChangeClientModal, setShowChangeClientModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState<WorkOrderVendor | null>(null);
  const [pendingVendorAssignment, setPendingVendorAssignment] = useState<{
    vendorId: number;
    isPrimary: boolean;
    notes: string;
  } | null>(null);
  const [complianceWarning, setComplianceWarning] = useState<VendorCompliance | null>(null);
  const [showComplianceOverrideModal, setShowComplianceOverrideModal] = useState(false);
  const { hasFeature } = useSubscription();
  const hasVendorCompliance = hasFeature('hasVendorCompliance');
  const [searchParams] = useSearchParams();
  const fromPage = searchParams.get('from') || 'workorders';

  React.useEffect(() => {
    if (fromPage === 'workorders') {
      sessionStorage.setItem('restore-workorders-state', 'true');
    } else if (fromPage === 'dashboard') {
      sessionStorage.setItem('restore-dashboard-state', 'true');
    }
  }, [fromPage]);


  // Fetch work order
  const {
    data: workOrderResponse,
    isLoading: workOrderLoading,
    error: workOrderError
  } = useQuery({
    queryKey: ['workorder', id],
    queryFn: () => apiService.getWorkOrder(parseInt(id!)),
    enabled: !!id,
    retry: 1,
    retryDelay: 1000
  });

  // Fetch company users for assignment dropdown
  const {
    data: companyUsersResponse,
    isLoading: companyUsersLoading,
    error: companyUsersError
  } = useQuery({
    queryKey: ['company-users'],
    queryFn: () => apiService.getCompanyUsers()
  });

  // Debug company users
  console.log('Company users response:', companyUsersResponse);
  console.log('Company users data:', companyUsersResponse?.data);
  console.log('Company users error:', companyUsersError);


  // Fetch company users
  const {
    data: usersResponse,
    isLoading: usersLoading
  } = useQuery({
    queryKey: ['company-users'],
    queryFn: () => apiService.getCompanyUsers(),
    retry: 1,
    retryDelay: 1000
  });

  // Fetch statuses
  const {
    data: statusesResponse,
    isLoading: statusesLoading
  } = useQuery({
    queryKey: ['statuses'],
    queryFn: () => apiService.getStatuses(),
    retry: 1
  });

  // Fetch active templates
  const {
    data: templatesResponse,
    isLoading: templatesLoading,
    error: templatesError
  } = useQuery({
    queryKey: ['active-templates'],
    queryFn: () => apiService.getActiveWorkOrderTemplates(),
    retry: 1
  });

  // Fetch active categories
  const {
    data: categoriesResponse,
    isLoading: categoriesLoading,
    error: categoriesError
  } = useQuery({
    queryKey: ['active-categories'],
    queryFn: () => apiService.getActiveCategories(),
    retry: 1
  });

  const workOrder = workOrderResponse?.data;

  // Fetch equipment for the client and location
  const {
    data: equipmentResponse,
    isLoading: equipmentLoading,
    error: equipmentError
  } = useQuery({
    queryKey: ['equipment-for-work-order', workOrder?.clientId, workOrder?.clientLocationId],
    queryFn: async () => {
      const response = await apiService.getEquipment(1, 1000);
      const allEquipment = response.data?.data || [];
      // Filter by client and location if available
      return allEquipment.filter((eq: any) => {
        if (workOrder && eq.clientId !== workOrder.clientId) return false;
        if (workOrder?.clientLocationId && eq.clientLocationId !== workOrder.clientLocationId) return false;
        return true;
      });
    },
    enabled: !!workOrder?.clientId,
    retry: 1
  });

  // Fetch eligible client locations for this work order's client
  const { data: locationsResponse, isLoading: locationsLoading } = useQuery({
    queryKey: ['client-eligible-locations', workOrder?.clientId],
    queryFn: () => apiService.getEligibleClientLocations(workOrder!.clientId),
    enabled: !!workOrder?.clientId,
    retry: 1
  });
  const locations: ClientLocationDto[] = locationsResponse?.data || [];

  const users = usersResponse?.data || [];
  const statuses = statusesResponse?.data || [];
  const templates = templatesResponse?.data || [];
  const categories = categoriesResponse?.data || [];
  const equipment = equipmentResponse || [];

  // Form setup
  const {
    control,
    handleSubmit,
    // watch, // Unused for now
    reset,
    formState: { errors, isValid }
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      assignedUserId: workOrder?.assignedUserId || '',
      // location is optional (nullable FK)
      priorityId: workOrder?.priorityId || 0,
      statusId: workOrder?.statusId || 1, // Default to first status instead of 0
      categoryId: workOrder?.categoryId || undefined,
      equipmentId: workOrder?.equipmentId || undefined,
      dueDate: workOrder?.dueDate ? new Date(workOrder.dueDate).toISOString().split('T')[0] : '',
      externalWorkOrderNumber: workOrder?.externalWorkOrderNumber || '',
      nteAmount: workOrder?.nteAmount ?? undefined,
      templateId: workOrder?.templateId || undefined,
      description: workOrder?.description || ''
    },
    mode: 'onChange'
  });

  // Reset form when work order data loads
  useEffect(() => {
    if (workOrder) {
      reset({
        assignedUserId: workOrder.assignedUserId || '',
        // no clientLocationId in form; we'll handle below via separate control
        priorityId: workOrder.priorityId || 0,
        statusId: workOrder.statusId || 1, // Default to first status instead of 0
        categoryId: workOrder.categoryId || undefined,
        equipmentId: workOrder.equipmentId || undefined,
        dueDate: workOrder.dueDate ? new Date(workOrder.dueDate).toISOString().split('T')[0] : '',
        externalWorkOrderNumber: workOrder.externalWorkOrderNumber || '',
        nteAmount: workOrder.nteAmount ?? undefined,
        templateId: workOrder.templateId || undefined,
        description: workOrder.description || ''
      });

      // Description will be handled by the WYSIWYGEditor component
    }
  }, [workOrder, reset]);

  // Update assigned user mutation
  const updateAssignedUserMutation = useMutation({
    mutationFn: (assignedUserId: string) => {
      if (!workOrder?.clientId) {
        throw new Error('Client ID is required for work order update');
      }

      const updateData = {
        clientId: workOrder.clientId,
        assignedUserId: assignedUserId,
        priorityId: workOrder.priorityId,
        statusId: workOrder.statusId,
        categoryId: workOrder.categoryId || undefined,
        dueDate: workOrder.dueDate,
        templateId: workOrder.templateId || undefined,
        externalWorkOrderNumber: workOrder.externalWorkOrderNumber || undefined,
        description: workOrder.description || '',
        incurredCost: workOrder.incurredCost ?? null,
        incurredCostNote: workOrder.incurredCostNote ?? null,
        proposedCost: workOrder.proposedCost ?? null,
        actualCost: workOrder.actualCost ?? null,
      };
      return apiService.updateWorkOrder(parseInt(id!), updateData);
    },
    onSuccess: () => {
      toast.success('Work order assignment updated successfully');
      queryClient.invalidateQueries({ queryKey: ['workorder', id] });
      queryClient.invalidateQueries({ queryKey: ['workorders'] });
    },
    onError: (error: any) => {
      toast.error('Error updating work order assignment');
      console.error('Error updating work order:', error);
    }
  });

  // Update mutation
  const updateWorkOrderMutation = useMutation({
    mutationFn: (data: FormData) => {
      if (!workOrder?.clientId) {
        throw new Error('Client ID is required for work order update');
      }

      const updateData = {
        clientId: workOrder.clientId,
        clientLocationId: workOrder.clientLocationId,
        vendors: (workOrder.vendors ?? []).map((v) => ({
          vendorId: v.vendorId,
          isPrimary: v.isPrimary,
          status: v.status,
          notes: v.notes,
        })),
        assignedUserId: data.assignedUserId || undefined,
        priorityId: data.priorityId && data.priorityId > 0 ? data.priorityId : undefined,
        statusId: data.statusId && data.statusId > 0 ? data.statusId : undefined,
        categoryId: data.categoryId || undefined,
        equipmentId: data.equipmentId || undefined,
        dueDate: data.dueDate || workOrder.dueDate,
        templateId: data.templateId || undefined,
        externalWorkOrderNumber: data.externalWorkOrderNumber || undefined,
        description: data.description,
        signedOff: workOrder.signedOff,
        invoiced: workOrder.invoiced,
        nteAmount: data.nteAmount ?? undefined,
        incurredCost: workOrder.incurredCost ?? null,
        incurredCostNote: workOrder.incurredCostNote ?? null,
        proposedCost: workOrder.proposedCost ?? null,
        actualCost: workOrder.actualCost ?? null,
        quoteRequested: workOrder.quoteRequested ?? false,
        pricingReceived: workOrder.pricingReceived ?? false,
        photosReceived: workOrder.photosReceived ?? false,
        invoiceReceived: workOrder.invoiceReceived ?? false,
      };
      return apiService.updateWorkOrder(parseInt(id!), updateData);
    },
    onSuccess: () => {
      toast.success('Work order updated successfully');
      queryClient.invalidateQueries({ queryKey: ['workorders'] });
      queryClient.invalidateQueries({ queryKey: ['workorder', id] });
      if (fromPage === 'jcs') {
        navigate(`/workorders/${id}/contact`);
      } else if (fromPage === 'dispatcher/dashboard') {
        navigate('/dispatcher/dashboard');
      } else {
        navigate('/workorders');
      }
    },
    onError: (error: any) => {
      toast.error('Failed to update work order');
      console.error('Update error:', error);
    }
  });

  // Add vendor mutation
  const addVendorMutation = useMutation({
    mutationFn: async (vendorData: {
      workOrderId: number;
      vendorId: number;
      isPrimary?: boolean;
      notes?: string;
      acknowledgeComplianceWarning?: boolean;
      complianceOverrideReason?: string;
    }) => {
      return await apiService.addVendorToWorkOrder(vendorData.workOrderId, {
        vendorId: vendorData.vendorId,
        isPrimary: vendorData.isPrimary || false,
        status: 'Assigned',
        notes: vendorData.notes || '',
        acknowledgeComplianceWarning: vendorData.acknowledgeComplianceWarning,
        complianceOverrideReason: vendorData.complianceOverrideReason,
      });
    },
    onSuccess: (response) => {
      if (response.success && response.data?.assignment) {
        toast.success('Vendor added successfully!');
        queryClient.invalidateQueries({ queryKey: ['workorder', id] });
        setShowComplianceOverrideModal(false);
        setComplianceWarning(null);
        setPendingVendorAssignment(null);
      } else if (hasVendorCompliance && response.data?.requiresComplianceAcknowledgment && response.data.complianceWarning) {
        setComplianceWarning(response.data.complianceWarning);
        setShowComplianceOverrideModal(true);
      } else {
        toast.error(response.message || 'Failed to add vendor');
      }
    },
    onError: (error) => {
      console.error('Add vendor error:', error);
      toast.error('Failed to add vendor');
    }
  });

  // Remove vendor mutation
  const removeVendorMutation = useMutation({
    mutationFn: async (vendorData: { workOrderId: number; vendorId: number }) => {
      return await apiService.removeVendorFromWorkOrder(vendorData.workOrderId, vendorData.vendorId);
    },
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Vendor removed successfully!');
        queryClient.invalidateQueries({ queryKey: ['workorder', id] });
      } else {
        toast.error('Failed to remove vendor');
      }
    },
    onError: (error) => {
      console.error('Remove vendor error:', error);
      toast.error('Failed to remove vendor');
    }
  });

  // Set primary vendor mutation
  const setPrimaryVendorMutation = useMutation({
    mutationFn: async (vendorData: { workOrderId: number; vendorId: number }) => {
      return await apiService.setPrimaryVendor(vendorData.workOrderId, vendorData.vendorId);
    },
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Primary vendor updated successfully!');
        queryClient.invalidateQueries({ queryKey: ['workorder', id] });
      } else {
        toast.error('Failed to update primary vendor');
      }
    },
    onError: (error) => {
      console.error('Set primary vendor error:', error);
      toast.error('Failed to update primary vendor');
    }
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      // Transform empty strings to undefined for optional fields
      const transformedData = {
        ...data,
        categoryId: data.categoryId === '' ? undefined : data.categoryId,
        templateId: data.templateId === '' ? undefined : data.templateId
      };
      updateWorkOrderMutation.mutate(transformedData);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Vendor management handlers
  const handleAddVendor = (
    vendor: VendorSearchDto,
    complianceOverride?: { acknowledgeComplianceWarning: boolean; complianceOverrideReason: string }
  ) => {
    if (!workOrder) return;
    
    const isFirstVendor = workOrder.vendors.length === 0;
    const assignment = {
      workOrderId: workOrder.id,
      vendorId: vendor.id,
      isPrimary: isFirstVendor,
      notes: '',
      acknowledgeComplianceWarning: complianceOverride?.acknowledgeComplianceWarning,
      complianceOverrideReason: complianceOverride?.complianceOverrideReason,
    };

    setPendingVendorAssignment({
      vendorId: vendor.id,
      isPrimary: isFirstVendor,
      notes: '',
    });

    addVendorMutation.mutate(assignment);
  };

  const handleComplianceOverrideConfirm = (reason: string) => {
    if (!workOrder || !pendingVendorAssignment) return;

    addVendorMutation.mutate({
      workOrderId: workOrder.id,
      vendorId: pendingVendorAssignment.vendorId,
      isPrimary: pendingVendorAssignment.isPrimary,
      notes: pendingVendorAssignment.notes,
      acknowledgeComplianceWarning: true,
      complianceOverrideReason: reason,
    });
  };

  const handleRemoveVendor = (vendor: WorkOrderVendor) => {
    setVendorToDelete(vendor);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteVendor = () => {
    if (!workOrder || !vendorToDelete) return;
    
    removeVendorMutation.mutate({
      workOrderId: workOrder.id,
      vendorId: vendorToDelete.vendorId
    });
    
    setShowDeleteConfirm(false);
    setVendorToDelete(null);
  };

  const cancelDeleteVendor = () => {
    setShowDeleteConfirm(false);
    setVendorToDelete(null);
  };

  const handleSetPrimary = (vendorId: number) => {
    if (!workOrder) return;
    
    setPrimaryVendorMutation.mutate({
      workOrderId: workOrder.id,
      vendorId: vendorId
    });
  };

  const handleAssignmentChange = (assignedUserId: string) => {
    updateAssignedUserMutation.mutate(assignedUserId);
  };


  if (workOrderLoading || usersLoading || statusesLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="text-gray-500">Loading work order...</div>
        </div>
      </div>
    );
  }

  if (workOrderError || !workOrder) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="text-red-600 text-lg font-medium">Error loading work order</div>
          <div className="text-gray-500 mt-2">Work order not found or error occurred</div>
          <button
            onClick={() => navigate('/workorders')}
            className="mt-4 btn-secondary"
          >
            Back to Work Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Ultra Compact Header */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start space-x-3">
            <button
              onClick={() => {
                if (fromPage === 'workorders') {
                  sessionStorage.setItem('restore-workorders-state', 'true');
                  navigate('/workorders');
                } else if (fromPage === 'dashboard') {
                  sessionStorage.setItem('restore-dashboard-state', 'true');
                  navigate('/dashboard');
                } else if (fromPage === 'dispatcher/dashboard') {
                  navigate('/dispatcher/dashboard');
                } else if (fromPage === 'jcs') {
                  navigate(`/workorders/${id}/contact`);
                } else {
                  navigate(-1);
                }
              }}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-lg md:text-xl font-semibold text-gray-900">Edit Work Order</h1>
                <span className={`px-2 py-1 text-xs rounded-full ${workOrder.priorityColorCode ? hexToTailwindClasses(workOrder.priorityColorCode) : 'bg-gray-100 text-gray-800'}`}>{workOrder.priorityName}</span>
                <span className="px-2 py-1 bg-accent-color-100 text-accent-color-800 text-xs rounded-full">{workOrder.statusName}</span>
              </div>
              <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
                <span className="font-medium">{workOrder.workOrderNumber}</span>
                <span>External: {workOrder.externalWorkOrderNumber || '-'}</span>
              
              </div>
            </div>
          </div>
          
          {/* Assignment & Dates - Right Side */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm md:justify-end">
            <div className="flex items-center space-x-2 min-w-[220px]">
              <span className="text-gray-500 shrink-0">Assigned to:</span>
              <SearchableSelect
                className="min-w-[200px]"
                options={[
                  { value: '', label: 'Unassigned' },
                  ...((companyUsersResponse?.data || []) as CompanyUser[])
                    .filter((user) => !!user.id)
                    .map((user) => ({
                      value: user.id,
                      label: `${user.firstName} ${user.lastName}`.trim() || user.email || 'Unknown user',
                    })),
                ]}
                value={workOrder.assignedUserId || ''}
                onChange={(value) => handleAssignmentChange(String(value || ''))}
                placeholder={companyUsersLoading ? 'Loading...' : companyUsersError ? 'Error loading users' : 'Select user'}
                searchPlaceholder="Search users..."
                disabled={updateAssignedUserMutation.isPending || companyUsersLoading}
                allowClear={false}
                aria-label="Assigned to"
              />
            </div>
            <div className="flex items-start gap-6 flex-wrap">
              <div className="flex flex-col items-end">
                <span className="text-gray-500">Created:</span>
                <span className="text-right">{new Date(workOrder.createdDate).toLocaleDateString()}</span>
                <span className="text-right text-xs text-gray-600">{new Date(workOrder.createdDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-gray-500">Due:</span>
                <span className="text-right font-medium">{new Date(workOrder.dueDate).toLocaleDateString()}</span>
                {(() => {
                  const today = new Date();
                  const dueDate = new Date(workOrder.dueDate);
                  const diffTime = dueDate.getTime() - today.getTime();
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  if (diffDays < 0) {
                    return <span className="text-right text-xs text-red-600">{Math.abs(diffDays)} days overdue</span>;
                  } else if (diffDays === 0) {
                    return <span className="text-right text-xs text-orange-600">Due today</span>;
                  } else if (diffDays <= 3) {
                    return <span className="text-right text-xs text-orange-600">{diffDays} days left</span>;
                  } else {
                    return <span className="text-right text-xs text-green-600">{diffDays} days left</span>;
                  }
                })()}
              </div>
              <div className="flex flex-col items-end">
                <span className="text-gray-500">Updated:</span>
                <span className="text-right">{new Date(workOrder.updatedDate).toLocaleDateString()}</span>
                <span className="text-right text-xs text-gray-600">{new Date(workOrder.updatedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            </div>
            {updateAssignedUserMutation.isPending && (
              <div className="text-xs text-accent-color">Updating...</div>
            )}
          </div>
        </div>
      </div>

      {/* Client Info & Vendor Management Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Client Information */}
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700 flex items-center">
              <Users className="h-4 w-4 mr-1" />
              Client Management
            </h3>
            <button
              type="button"
              onClick={() => setShowChangeClientModal(true)}
              className="flex items-center px-3 py-1 text-xs bg-accent-color text-white rounded hover:bg-accent-color-600 transition-colors"
              aria-label="Change client"
            >
              Change Client
            </button>
          </div>
          <div className="space-y-1 text-sm">
            <div className="font-medium text-gray-900">{workOrder.clientName}</div>
            {workOrder.clientPhone && (
              <div className="flex items-center space-x-1">
                <Phone className="h-3 w-3 text-gray-400" />
                <span className="text-gray-600">{formatPhoneNumber(workOrder.clientPhone)}</span>
              </div>
            )}
            {workOrder.clientEmail && (
              <div className="flex items-center space-x-1">
                <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a 
                  href={`mailto:${workOrder.clientEmail}`}
                  className="text-accent-color hover:text-accent-color-800 hover:underline"
                >
                  {workOrder.clientEmail}
                </a>
              </div>
            )}
            {(workOrder.clientAddress || workOrder.clientCity || workOrder.clientState || workOrder.clientZipCode) && (
              <div className="text-gray-600">
                {workOrder.clientAddress && <div>{workOrder.clientAddress}</div>}
                {(workOrder.clientCity || workOrder.clientState || workOrder.clientZipCode) && (
                  <div>
                    {workOrder.clientCity && workOrder.clientCity}
                    {workOrder.clientCity && workOrder.clientState && ', '}
                    {workOrder.clientState && workOrder.clientState}
                    {workOrder.clientZipCode && ` ${workOrder.clientZipCode}`}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Vendor Management */}
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700 flex items-center">
              <Users className="h-4 w-4 mr-1" />
              Vendor Management
            </h3>
            <button
              onClick={() => setShowVendorModal(true)}
              className="flex items-center px-3 py-1 text-xs bg-accent-color text-white rounded hover:bg-accent-color-600 transition-colors"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Vendor
            </button>
          </div>
          
          {/* Vendors List */}
          {workOrder.vendors && workOrder.vendors.length > 0 ? (
            <div className="space-y-2">
              {workOrder.vendors.map((vendor: WorkOrderVendor) => (
                <div key={vendor.id} className="group relative flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center flex-wrap gap-2">
                    <span className="text-sm font-medium text-gray-900">{vendor.vendorName}</span>
                    {vendor.isPrimary && (
                      <Star className="h-3 w-3 text-yellow-500 fill-current" />
                    )}
                    {hasVendorCompliance && vendor.complianceStatusLabel && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getComplianceStatusClasses(vendor.complianceStatus, vendor.complianceStatusLabel)}`}>
                        {vendor.complianceStatusLabel}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">({vendor.status})</span>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center space-x-1">
                    {!vendor.isPrimary && (
                      <button
                        onClick={() => handleSetPrimary(vendor.vendorId)}
                        className="h-7 w-7 inline-flex items-center justify-center border rounded-md text-gray-700 border-gray-300 hover:bg-gray-100"
                        title="Set as Primary"
                      >
                        <Star className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveVendor(vendor)}
                      className="h-7 w-7 inline-flex items-center justify-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
                      title="Remove Vendor"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500 text-center py-4">
              No vendors assigned
            </div>
          )}
        </div>
      </div>

      {/* Edit Form */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6">Edit Work Order Details</h3>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Client Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Location
              </label>
              <select
                value={workOrder.clientLocationId || ''}
                onChange={(e) => {
                  const clientLocationId = e.target.value ? parseInt(e.target.value) : undefined;
                  // Partial patch of work order for location change
                  apiService.patchWorkOrder(parseInt(id!), {
                    clientId: workOrder.clientId,
                    clientLocationId,
                    assignedUserId: workOrder.assignedUserId || undefined,
                    priorityId: workOrder.priorityId,
                    statusId: workOrder.statusId,
                    categoryId: workOrder.categoryId || undefined,
                    dueDate: workOrder.dueDate,
                    templateId: workOrder.templateId || undefined,
                    externalWorkOrderNumber: workOrder.externalWorkOrderNumber || undefined,
        
                    description: workOrder.description || ''
                  }).then(() => {
                    queryClient.invalidateQueries({ queryKey: ['workorder', id] });
                    toast.success('Location updated');
                  }).catch(() => toast.error('Failed to update location'));
                }}
                disabled={locationsLoading}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">No specific location</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.isPrimary ? 'Primary - ' : ''}{loc.locationName} {loc.city ? `(${loc.city}${loc.state ? ', ' + loc.state : ''})` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">Primary location is used by default if none selected.</p>
            </div>
            {/* Assigned User */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assigned User
              </label>
              <Controller
                name="assignedUserId"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    options={[
                      { value: '', label: 'Unassigned' },
                      ...users
                        .filter((user: CompanyUser) => !!user.id)
                        .map((user: CompanyUser) => ({
                          value: user.id,
                          label: `${user.firstName} ${user.lastName}`.trim() || user.email || 'Unknown user',
                        })),
                    ]}
                    value={field.value || ''}
                    onChange={(value) => field.onChange(String(value || ''))}
                    placeholder="Select a user"
                    searchPlaceholder="Search users..."
                    disabled={usersLoading}
                    error={errors.assignedUserId?.message}
                    aria-label="Assigned user"
                  />
                )}
              />
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority <span className="text-red-500">*</span>
              </label>
              <PriorityDropdown
                name="priorityId"
                control={control}
                error={errors.priorityId}
                required={true}
                placeholder="Select Priority"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <Controller
                name="statusId"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    disabled={statusesLoading}
                  >
                    <option value={0}>Select Status</option>
                    {statuses.map(status => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.statusId && (
                <p className="mt-1 text-sm text-red-600">{errors.statusId.message}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    disabled={categoriesLoading}
                  >
                    <option value="">Select a category (optional)</option>
                    {categories.map((category: Category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {categoriesError && (
                <p className="mt-1 text-sm text-red-600">Error loading categories</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Choose a category to classify this work order
              </p>
            </div>

            {/* Equipment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Equipment
              </label>
              <Controller
                name="equipmentId"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    disabled={equipmentLoading}
                  >
                    <option value="">Select equipment (optional)</option>
                    {equipment.map((eq: any) => (
                      <option key={eq.id} value={eq.id}>
                        {eq.name} - {eq.clientLocationName || 'No Location'}
                      </option>
                    ))}
                  </select>
                )}
              />
              {equipmentError && (
                <p className="mt-1 text-sm text-red-600">Error loading equipment</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Link this work order to a specific equipment
              </p>
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date <span className="text-red-500">*</span>
              </label>
              <Controller
                name="dueDate"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                )}
              />
              {errors.dueDate && (
                <p className="mt-1 text-sm text-red-600">{errors.dueDate.message}</p>
              )}
            </div>

            {/* External Work Order Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                External Work Order Number
              </label>
              <Controller
                name="externalWorkOrderNumber"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder="Enter external work order number"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                )}
              />
            </div>

            {/* NTE Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                NTE Amount (Not To Exceed)
              </label>
              <Controller
                name="nteAmount"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 dark:text-gray-400 sm:text-sm">$</span>
                    </div>
                    <input
                      {...field}
                      type="number"
                      step="1"
                      min="0"
                      placeholder="0.00"
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color placeholder-gray-400 dark:placeholder-gray-500"
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                        field.onChange(value);
                      }}
                    />
                  </div>
                )}
              />
              {errors.nteAmount && (
                <p className="mt-1 text-sm text-red-600">{errors.nteAmount.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Set a maximum amount for this work order (optional)
              </p>
            </div>

            {/* Template Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Work Order Template
              </label>
              <Controller
                name="templateId"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    disabled={templatesLoading}
                  >
                    <option value="">Select a template (optional)</option>
                    {templates.map((template: WorkOrderTemplate) => (
                      <option key={template.id} value={template.id}>
                        {template.name} {template.isDefault ? '(Default)' : ''}
                      </option>
                    ))}
                  </select>
                )}
              />
              {templatesError && (
                <p className="mt-1 text-sm text-red-600">Error loading templates</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Choose a template to format the work order document
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <WYSIWYGEditor
                  key={`description-${workOrder.id}`}
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder="Enter work order description..."
                />
              )}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Minimum 10 characters required
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                if (fromPage === 'jcs') {
                  navigate(`/workorders/${id}/contact`);
                } else if (fromPage === 'dispatcher/dashboard') {
                  navigate('/dispatcher/dashboard');
                } else {
                  navigate('/workorders');
                }
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !isValid}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Updating...' : 'Update Work Order'}
            </button>
          </div>
        </form>
      </div>

      {/* Vendor Selection Modal */}
      {workOrder && (
        <VendorSelectionModal
          isOpen={showVendorModal}
          onClose={() => setShowVendorModal(false)}
          onSelectVendor={handleAddVendor}
          clientZipCode={workOrder.clientZipCode || ''}
          existingVendors={workOrder.vendors || []}
        />
      )}

      {workOrder && (
        <ChangeWorkOrderClientModal
          isOpen={showChangeClientModal}
          workOrderId={id!}
          workOrder={workOrder}
          onClose={() => setShowChangeClientModal(false)}
        />
      )}

      {hasVendorCompliance && (
      <ComplianceOverrideModal
        isOpen={showComplianceOverrideModal}
        onClose={() => {
          setShowComplianceOverrideModal(false);
          setComplianceWarning(null);
          setPendingVendorAssignment(null);
        }}
        compliance={complianceWarning}
        onConfirm={handleComplianceOverrideConfirm}
        isLoading={addVendorMutation.isPending}
      />
      )}

      {/* Delete Vendor Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm && !!vendorToDelete}
        onClose={cancelDeleteVendor}
        title="Remove Vendor"
        maxWidth="md"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">
                Are you sure you want to remove <strong>{vendorToDelete?.vendorName}</strong> from this work order?
              </p>
            </div>
          </div>
          
          {vendorToDelete?.isPrimary && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Star className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Warning:</strong> This vendor is currently set as the primary vendor. 
                    Removing them will leave the work order without a primary vendor.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={cancelDeleteVendor}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDeleteVendor}
              disabled={removeVendorMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {removeVendorMutation.isPending ? 'Removing...' : 'Remove Vendor'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EditWorkOrderPage;
